import _ from 'lodash';
import moment from 'moment/moment';
import obsScoreConfig from '../../../../app/obsScoreConfig';
import {getConfig} from '../../../../util/configUtil';
import Formatter from '../../../../util/formatter';

const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');
const isRetroObs = getConfig('observations.retrospective', false);
const newRiskAssessment = getConfig('observations.newRiskAssessment', false);
const neuroNextObsTimeAvailable = getConfig('neuroNextObsTimeAvailable', false);

export const obsStatuses = {
    new: 'new',
    completed: 'completed',
    cancelled: 'cancelled',
    partial: 'partial',
    notRecorded: 'notRecorded',
    patientRefused: 'patientRefused',
    equipment: 'equipment',
    patientCondition: 'patientCondition',
    suspended: 'suspended',
    unpaused: 'unpaused',
    deferred: 'deferred',
    notTestable: 'notTestable',
    noReaction: 'noReaction',
};

// Must be in correct order from low to high
export const riskLevels = {
    none: '',
    continue: 'continue',
    low: 'low',
    mediumLow: 'mediumLow',
    medium: 'medium',
    high: 'high',
    emergency: 'emergency',
};

export const obsTypes = {
    news: 'news',
    pews: 'pews',
    neuro: 'neuro',
};

export const obsAges = {
    _0_1: '0_1',
    _1_5: '1_5',
    _5_12: '5_12',
    _12_18: '12_18',
    _5_16: '5_16',
    _16_99: '16_99',
};

export const emitChangedWait = 100;

function getArmType(arm) {
    if (arm) {
        // eslint-disable-next-line default-case
        switch (arm) {
            case 'left':
                return ' (LA)';
            case 'right':
                return ' (RA)';
            case 'leftLeg':
                return ' (LL)';
            case 'rightLeg':
                return ' (RL)';
        }
    }
    return '';
}

export const getObsScore = (observation,ageType,ageDict ,value) => {
    if(value === undefined || value === null) return 0;
    let age = 0;
    if (ageType === obsTypes.news) {
        age = 18;
    } else {
        if (ageDict === obsAges._0_1) {
            age = 0.1;
        } else if (ageDict === obsAges._1_5) {
            age = 1;
        } else if (ageDict === obsAges._5_12) {
            age = 5;
        } else if (ageDict === obsAges._12_18) {
            age = 13;
        }
    }
    let observations = _.find(obsScoreConfig.ages, (observations) => {
        return age >= observations.min && age < observations.max;
    }).observations;
    if(observations) {
        let _observation = observations[observation];
        if(_observation) {
            if(observation === "respiratoryDistress") {
                return _observation[value];
            }
            return _.find(_observation, (obs) => {
                return value >= obs.min && value < obs.max;
            }).score;
        }
    }
    return 0;
};

export const mapObservationToAPI = (obs) => {
    const mapValues = (name, ...fields) => {
        let res = {
            name: name,
            code: name,
            // type
            values: [],
            // range
            // unit
            status: obs[name].status ? obs[name].status : obs[name].accepted ? obsStatuses.completed : null,
            observationDate: obs[name].acceptDate ? obs[name].acceptDate.toISOString() : null,
            // setId
            score: obs[name].score,
        };

        if (fields && res.status === obsStatuses.completed) {
            _.forEach(fields, (field) => {
                let value = obs[name][field];
                if (value && moment.isMoment(value)) {
                    value = value.toISOString();
                }
                res.values.push({
                    name: field,
                    value: value,
                });
            });
        }

        return res;
    };

    let apiObservation = {
        __id: obs.__id,
        // patientId,
        // href
        department: 'ED',
        // requested
        investigation: obs.type,
        // investigationCode
        location: 'ED',
        // locationName
        status: obs.status,
        // requestingClinician
        // received
        // sampleCollected
        // review
        tests: [],
        // reported
        // linkedReports
        // assignedClinician
        // examSampleNo
        incomingSystem: 'agyle',
        // color
        examinationStart: obs.startedDate ? obs.startedDate.toISOString() : null,
        examinationEnd: obs.completedDate ? obs.completedDate.toISOString() : null,
        // canceledBy
        // canceledByName
        // canceledDate
        totalScore: obs.totalScore,
        nextObsDate: obs.nextObsDate,
        nextObsStatus: obs.status === 'partial' ? null : obs.nextObsStatus,
        nextObsReason: obs.nextObsReason,
        completedBy: obs.completedBy,
        completedByName: obs.completedByFullName,
        attendanceCustomId: obs.attendanceCustomId,
        metadata: {
            clinicalResponse: obs.clinicalResponse,
            riskLevel: obs.riskLevel,
            prevRiskLevel: obs.prevRiskLevel,
            prevTotalScore: obs.prevTotalScore,
            extraOption1: obs.extraOption1,
            extraOption1Reason: obs.extraOption1Reason,
            extraOption2: obs.extraOption2,
            extraOption3: obs.extraOption3,
            extraOption3Reason: obs.extraOption3Reason,
            extraOption4: obs.extraOption4,
            extraOption4Reason: obs.extraOption4Reason,
            ambulanceObs: obs.ambulanceObs,
        },
        patientsInWardsRef: [obs.patientInWardId],
    };

    if (obs.type !== obsTypes.neuro) {
        apiObservation.tests.push(mapValues('respiration', 'breaths'));
        apiObservation.tests.push(mapValues('airOxygen', 'airType', 'device', 'inspiredOxygen'));
        apiObservation.tests.push(mapValues('spo2scale', 'hypercapnic', 'spo2scale'));
        apiObservation.tests.push(mapValues('pulseRate', 'pulseType', 'pulseRate'));
        apiObservation.tests.push(mapValues('temperature', 'temperature'));
        apiObservation.tests.push(mapValues('painScore', 'painScore'));
        apiObservation.tests.push(mapValues('consciousness', 'consciousnessLevel'));
    }
    if (obs.type === obsTypes.news) {
        apiObservation.tests.push(mapValues('bloodPressure', 'systolicPressure', 'diastolicPressure', 'arm', 'optionalBP', 'optionalSystolicPressure', 'optionalDiastolicPressure', 'optionalArm'));
    } else if (obs.type === obsTypes.pews) {
        apiObservation.tests.push(mapValues('bloodPressure', 'systolicPressure', 'diastolicPressure'));
        apiObservation.tests.push(mapValues('distress', 'distress'));
        apiObservation.tests.push(mapValues('crt', 'crt'));
        apiObservation.tests.push(mapValues('wheezing', 'wheezing'));
        apiObservation.tests.push(mapValues('stridor', 'stridor'));
        apiObservation.tests.push(mapValues('apnoea', 'apnoea'));
    } else if (obs.type === obsTypes.neuro) {
        apiObservation.tests.push(mapValues('eyesOpen', 'eyesOpenLevel', 'eyesClosedBySwelling'));
        apiObservation.tests.push(mapValues('bestVerbalResponse', 'response', 'tracheostomy'));
        apiObservation.tests.push(mapValues('bestMotorResponse', 'response'));
        apiObservation.tests.push(mapValues('pupils', 'reactionLeft', 'sizeLeft', 'closedLeft', 'reactionRight', 'sizeRight', 'closedRight'));
        apiObservation.tests.push(mapValues('limbMovementArms', 'movement', 'arm', 'secondMovement', 'secondArm'));
        apiObservation.tests.push(mapValues('limbMovementLegs', 'movement', 'leg', 'secondMovement', 'secondLeg'));
    }
    apiObservation.tests.push(mapValues('glucose', 'glucose', 'ketones'));

    return apiObservation;
};

export const mapObservationFromAPI = (apiObs, noBPExtendedVal = false) => {
    const extendedBPValue = getConfig('observations.extendedBPValue', false);
    let obs = {
        ...apiObs,
        __id: apiObs.__id,
        type: apiObs.type,
        startedDate: apiObs.examinationStart ? moment(apiObs.examinationStart) : apiObs.startedDate ? moment(apiObs.startedDate) : null,
        completedDate: apiObs.examinationEnd ? moment(apiObs.examinationEnd) : apiObs.completedDate ? moment(apiObs.completedDate) : null,
        completedByFullName: apiObs.completedByFullName || apiObs.completedByName,
        completedBy: apiObs.completedBy,
        status: apiObs.status,
        canceledDate: apiObs.canceledDate ? moment(apiObs.canceledDate) : null,
        canceledByFullName: apiObs.canceledByFullName || apiObs.canceledByName,
        canceledBy: apiObs.canceledBy,
        totalScore: apiObs.totalScore,
        nextObs: apiObs.metadata?.nextObs,
        nextObsDate: apiObs.nextObsDate ? moment(apiObs.nextObsDate) : null,
        nextObsStatus: apiObs.nextObsStatus,
        nextObsReason: apiObs.nextObsReason,
        clinicalResponse: apiObs.metadata?.clinicalResponse,
        riskLevel: apiObs.metadata?.riskLevel,
        prevTotalScore: apiObs.metadata?.prevTotalScore,
        prevRiskLevel: apiObs.metadata?.prevRiskLevel,
        attendanceCustomId: apiObs.attendanceCustomId,
        extraOption1: apiObs.metadata?.extraOption1,
        extraOption1Reason: apiObs.metadata?.extraOption1Reason,
        extraOption2: apiObs.metadata?.extraOption2,
        extraOption3: apiObs.metadata?.extraOption3,
        extraOption3Reason: apiObs.metadata?.extraOption3Reason,
        extraOption4: apiObs.metadata?.extraOption4,
        extraOption4Reason: apiObs.metadata?.extraOption4Reason,
        ambulanceObs: apiObs.metadata?.ambulanceObs,
        investigation: apiObs.investigation ? apiObs.investigation : '',
        patientsInWardsRef: apiObs.patientsInWardsRef ? apiObs.patientsInWardsRef : '',
    };

    _.forEach(apiObs.tests, (test) => {
        let name = test.name;
        let obsPart = {
            accepted: !!test.observationDate,
            acceptDate: test.observationDate ? moment(test.observationDate) : null,
            score: test.score,
            status: test.status || obsStatuses.completed,
        };
        _.forEach(test.values, (value) => {
            let val = value.value;

            // If extended BP Value is turned on add to a value which arm/leg was examined
            if (!noBPExtendedVal && test?.name === 'bloodPressure' && extendedBPValue) {
                if (value.name === 'optionalDiastolicPressure' || value.name === 'optionalSystolicPressure') {
                    const arm = _.find(test.values, {name: 'optionalArm'});
                    val = val + getArmType(arm?.value);
                } else if (value.name === 'diastolicPressure' || value.name === 'systolicPressure') {
                    const arm = _.find(test.values, {name: 'arm'});
                    val = val + getArmType(arm?.value);
                }
            }
            obsPart[value.name] = val;
        });
        obs[name] = obsPart;
    });
    return obs;
};

export const printValue = (obs, item) => {
    // Configs
    const dateFormat = getConfig('defaultDateTimeFormat');
    const extendedBPValue = getConfig('observations.extendedBPValue', false);

    let status = _.get(obs, item.statusField, obsStatuses.completed);
    if (item.type !== 'totalScore') {
        if (status === obsStatuses.notRecorded) {
            return 'NR';
        } else if (status === obsStatuses.patientRefused) {
            return 'PR';
        } else if (status === obsStatuses.equipment) {
            return 'EQ';
        } else if (status === obsStatuses.patientCondition) {
            return 'PC';
        } else if (status === obsStatuses.notTestable) {
            return 'NT';
        } else if (status === obsStatuses.noReaction) {
            return 'No reaction';
        }
    }
    let val = _.get(obs, item.field);
    if (item.description === 'Pupils Reaction Left' || item.description === 'Pupils Reaction Right') {
        // console.log(item);
        // console.log(val);
    }
    if (val !== undefined && val != null) {
        if (item.type === 'string') {
            // if (val === 'true') {
            //     return 'Yes';
            // } else if (val === 'false') {
            //     return 'No';
            // } else {
            return _.startCase(val.toString());
            // return val.toString();
            // }
        } else if (item.type === 'nextObs') {
            return val === 'new' ? 'Ongoing' : _.capitalize(val);
        } else if (item.type === 'date') {
            if (val && !moment.isMoment(val)) {
                val = moment(val);
            }
            if (item.format) {
                return val.format(item.format);
            } else {
                return val.format(dateFormat);
            }
        } else if (item.type === 'number') {
            if (item.hasOwnProperty('precision')) {
                return val.toFixed(item.precision);
            } else {
                return val.toString();
            }
        } else if (item.type === 'numberBP') {
            let armVal = '';
            if (extendedBPValue) {
                const armField = `bloodPressure.${item?.optionalArm ? 'optionalArm' : 'arm'}`;
                const arm = _.get(obs, armField);

                // eslint-disable-next-line default-case
                switch (arm) {
                    case 'left':
                        armVal = '(LA)';
                        break;
                    case 'right':
                        armVal = '(RA)';
                        break;
                    case 'leftLeg':
                        armVal = '(LL)';
                        break;
                    case 'rightLeg':
                        armVal = '(RL)';
                        break;
                }
            }

            if (val === 0) {
                return '-';
            } else if (item.hasOwnProperty('precision')) {
                let newVal = val.toFixed(item.precision);
                if (extendedBPValue) {
                    newVal = `${newVal} ${armVal}`;
                }
                return newVal;
            } else {
                let newVal = val.toString();
                if (extendedBPValue) {
                    newVal = `${newVal} ${armVal}`;
                }
                return newVal;
            }
        } else if (item.type === 'totalScore') {
            if (
                status === obsStatuses.partial ||
                status === obsStatuses.notRecorded
            ) {
                return val.toString() + ' - NR';
            } else if (status === obsStatuses.notTestable) {
                return 'NT';
            } else {
                return val.toString();
            }
        } else if (item.type === 'boolean') {
            // console.log("VAL:", val);
            return val ? 'Yes' : 'No';
        } else if (_.isFunction(item.type)) {
            return item.type(item, obs, val);
        } else if (item.type === 'riskLevel') {
            if (_.isEmpty(val)) return 'None';
            return _.startCase(val);
        } else {
            return val.toString();
        }
    }
    return item.default || '-';
};

export const observationColors = {
    red: '#FF0000',
    lime: '#00FF00',
    blue: '#0000FF',
    yellow: '#FFFF00',
    cyan: '#00FFFF',
    magenta: '#FF00FF',
    silver: '#C0C0C0',
    gray: '#808080',
    maroon: '#800000',
    olive: '#808000',
    green: '#008000',
    purple: '#800080',
    teal: '#008080',
    navy: '#000080',
};

export const colorAlpha = (color, alpha) => {
    return `rgba(${parseInt(color.substring(1, 3), 16)}, ${parseInt(
        color.substring(3, 5),
        16,
    )}, ${parseInt(color.substring(5, 7), 16)}, ${alpha})`;
};

export const checkObsStatus = (item) => {
    if (item?.status) {
        if (item.status === 'notRecorded') {
            return 'NR';
        } else if (item.status === 'patientRefused') {
            return 'PR';
        } else if (item.status === 'equipment') {
            return 'EQ';
        } else if (item.status === 'patientCondition') {
            return 'PC';
        } else {
            return 'completed';
        }
    }
    return '-';
};

// Map for comparison obs
// Val: [0]  Object path in Obs Object [1] Test Name, [2] Value Path in object
const DEFAULT_MAP = [
    ["startedDate", "Started", null],
    ["totalScore", "Score", null],
    ["respiration", "Respiratory Rate", "breaths"],
    ["airOxygen", "Inspired O₂%", "airType"],
    ["distress", "Respiratory Distresses", "distress.description"],
    ["wheezing", "Respiratory Distress Wheezing", "wheezing.description"],
    ["stridor", "Stridor", "stridor"],
    ["apnoea", "Apnoea", "apnoea"],
    ["spo2scale", "SpO₂", "spo2scale"],
    ["spo2scale", "Hypercapnic", "hypercapnic"],
    ["bloodPressure", "Blood Pressure Systolic", "systolicPressure"],
    ["bloodPressure", "Blood Pressure Diastolic", "diastolicPressure"],
    ["pulseRate", "Heart Rate", "pulseRate"],
    ["pulseRate", "Heart Rate Type", "pulseType"],
    ["consciousness", "Consciousness Level", "consciousnessLevel.description"],
    ["crt", "CRT", "crt"],
    ["temperature", "Temperature", "temperature"],
    ["painScore", "Pain Score", "painScore"],
    ["glucose", "Glucose", "glucose"],
    ["glucose", "Ketones", "ketones"],
    ["extraOption3", "Sepsis Suspected", null],
    ["status", "Status", null],
    ["completedByFullName", "Completed by", null],
];

export const addObservationsToExaminationInput = (selectedObs, setSelectedObs, setData, field) => {
    const mapNote = new Map();
    let type;

    const pushToMap = (key, val) => {
        const mapVal = mapNote.get(key);

        if (mapVal) {
            mapVal.push(val);
        } else {
            mapNote.set(key, [val]);
        }
    };

    _.forEach(selectedObs, obs => {
        type = obs.investigation;
        _.forEach(DEFAULT_MAP, map => {
            const value = map[2] ? _.get(obs[map[0]], map[2]) : obs[map[0]];
            const checkStatus = checkObsStatus(obs[map[0]]);

            if (checkStatus === 'completed' || !map[2])
                if (!_.isNull(value)) {
                    if (_.isBoolean(value))
                        value ? pushToMap(map[1], 'Yes') : pushToMap(map[1], 'No');
                    else if (moment.isMoment(value))
                        pushToMap(map[1], value.format(defaultDateTimeFormat));
                    else
                        map[0] === 'status' ? pushToMap(map[1], _.startCase(value)) : pushToMap(map[1], value);
                } else {
                    pushToMap(map[1], '-');
                }
            else
                pushToMap(map[1], checkStatus);
        });
    });

    const note = Formatter.formatObservationsMapToText(mapNote, type);

    if (note) {
        setData((prevState) => ({
            ...prevState,
            [field]: prevState[field] !== '' ? prevState[field] + '\n' + note : note,
        }));

        setSelectedObs([]);

        copyToClipboard(note);
    }
};

const copyToClipboard = (text) => {
    if (!navigator.clipboard) {
        return;
    }
    navigator.clipboard.writeText(text)
        .then(() => {
            console.log('Clipboard filled with observations');
        }).catch(err => console.error('Error: ', err));
};

const _getNextObsTypes = () => {
    let types = [obsTypes.news, obsTypes.pews];
    if (neuroNextObsTimeAvailable) types.push(obsTypes.neuro);
    return types;
};

export const printReactionValue = (item, obs, val) => {
    let closed, reaction;
    if (item.field === 'pupils.reactionLeft') {
        closed = obs?.pupils.closedLeft;
        reaction = obs?.pupils.reactionLeft;
    } else if (item.field === 'pupils.reactionRight') {
        closed = obs?.pupils.closedRight;
        reaction = obs?.pupils.reactionRight;
    }

    if (closed) {
        return 'C';
    } else if (reaction === true) {
        return 'Yes';
    } else if (reaction === false) {
        return 'No';
    }
    return '-';
};

// ObsNeuroPupils.printSizeValue = (item, obs, val) => {
export const printSizeValue = (item, obs, val) => {
    let closed, size;
    if (item.field === 'pupils.sizeLeft') {
        closed = obs?.pupils.closedLeft;
        size = obs?.pupils.sizeLeft;
    } else if (item.field === 'pupils.sizeRight') {
        closed = obs?.pupils.closedRight;
        size = obs?.pupils.sizeRight;
    }

    if (closed) {
        return 'C';
    } else {
        return size.toString();
    }
};

export const getObsDisplayValues = (type) => {
    return [
        {
            type: 'button',
            description: isRetroObs ? 'Obs assessed' : 'Obs completed',
        },
        {
            field: 'startedDate',
            description: 'Started',
            type: 'date',
            format: defaultDateTimeFormat,
            disableOnTable: true,
        },
        {
            field: 'totalScore',
            statusField: 'status',
            description: type === obsTypes.news ? 'NEWS Score'
                    : (type === obsTypes.pews ? 'PEWS Score' : 'GCS Score'),
            type: 'totalScore',
            scoreField: 'totalScore',
            chart: {
                enabled: true,
                yAxis: {
                    suggestedMax: 10,
                    suggestedMin: 0,
                    //
                    title: {
                        color: observationColors.red,
                    },
                    ticks: {
                        color: observationColors.red,
                    },
                },
                dataset: {
                    backgroundColor: observationColors.red,
                    borderColor: colorAlpha(observationColors.red, 0.2),
                },
            },
        },
        {
            field: 'riskLevel',
            description: 'Risk Level',
            type: 'riskLevel',
            scoreField: 'riskLevel',
            obsTypes: [newRiskAssessment ? obsTypes.pews : ''],
        },
        {
            field: 'respiration.breaths',
            statusField: 'respiration.status',
            description: 'Resp Rate',
            type: 'number',
            scoreField: 'respiration.score',
            obsTypes: [obsTypes.news, obsTypes.pews],
            chart: {
                enabled: true,
                yAxis: {
                    suggestedMax: 100,
                    suggestedMin: 0,
                    title: {
                        color: observationColors.lime,
                    },
                    ticks: {
                        color: observationColors.lime,
                    },
                },
                dataset: {
                    backgroundColor: observationColors.lime,
                    borderColor: colorAlpha(observationColors.lime, 0.2),
                },
            },
        },
        {
            field: 'distress.distress.description',
            statusField: 'distress.status',
            description: 'Resp Distress',
            type: 'string',
            scoreField: 'distress.score',
            obsTypes: [obsTypes.pews],
        },
        {
            field: 'wheezing.wheezing.description',
            statusField: 'wheezing.status',
            description: 'Wheezing',
            type: 'string',
            obsTypes: [obsTypes.pews],
        },
        {
            field: 'stridor.stridor',
            statusField: 'stridor.status',
            description: 'Stridor',
            type: 'boolean',
            obsTypes: [obsTypes.pews],
        },
        {
            field: 'apnoea.apnoea',
            statusField: 'apnoea.status',
            description: 'Apnoea',
            type: 'boolean',
            obsTypes: [obsTypes.pews],
        },
        {
            field: 'airOxygen.inspiredOxygen',
            statusField: 'airOxygen.status',
            description: 'Inspired O2',
            type: 'number',
            scoreField: 'airOxygen.score',
            obsTypes: [obsTypes.news, obsTypes.pews],
            chart: {
                enabled: true,
                yAxis: {
                    suggestedMax: 100,
                    suggestedMin: 0,
                    title: {
                        color: observationColors.teal,
                    },
                    ticks: {
                        color: observationColors.teal,
                    },
                },
                dataset: {
                    backgroundColor: observationColors.teal,
                    borderColor: colorAlpha(observationColors.teal, 0.2),
                },
            },
        },
        {
            field: 'spo2scale.spo2scale',
            statusField: 'spo2scale.status',
            description: 'SpO2',
            type: 'number',
            scoreField: 'spo2scale.score',
            obsTypes: [obsTypes.news, obsTypes.pews],
            chart: {
                enabled: true,
                yAxis: {
                    suggestedMax: 100,
                    suggestedMin: 0,
                    title: {
                        color: observationColors.maroon,
                    },
                    ticks: {
                        color: observationColors.maroon,
                    },
                },
                dataset: {
                    backgroundColor: observationColors.maroon,
                    borderColor: colorAlpha(observationColors.maroon, 0.2),
                },
            },
        },
        {
            field: 'spo2scale.hypercapnic',
            statusField: 'spo2scale.status',
            description: 'SpO2 Scale 2',
            type: 'boolean',
            obsTypes: [obsTypes.news, obsTypes.pews],
        },
        {
            field: 'bloodPressure.systolicPressure',
            statusField: 'bloodPressure.status',
            description: 'BP Systolic',
            type: 'numberBP',
            scoreField: 'bloodPressure.score',
            obsTypes: [obsTypes.news, obsTypes.pews],
            chart: {
                enabled: true,
                yAxis: {
                    suggestedMax: 200,
                    suggestedMin: 50,
                    title: {
                        color: observationColors.yellow,
                    },
                    ticks: {
                        color: observationColors.yellow,
                    },
                },
                dataset: {
                    backgroundColor: observationColors.yellow,
                    borderColor: colorAlpha(observationColors.yellow, 0.2),
                },
            },
            optionalArm: false,
        },
        {
            field: 'bloodPressure.diastolicPressure',
            statusField: 'bloodPressure.status',
            description: 'BP Diastolic',
            type: 'numberBP',
            scoreField: 'bloodPressure.score',
            obsTypes: [obsTypes.news, obsTypes.pews],
            chart: {
                enabled: true,
                yAxis: {
                    suggestedMax: 200,
                    suggestedMin: 50,
                    title: {
                        color: observationColors.yellow,
                    },
                    ticks: {
                        color: observationColors.yellow,
                    },
                },
                dataset: {
                    backgroundColor: observationColors.yellow,
                    borderColor: colorAlpha(observationColors.yellow, 0.2),
                },
            },
            optionalArm: false,
        },
        // {
        //     field: "bloodPressure.arm",
        //     statusField: "bloodPressure.status",
        //     description: t("BP Arm"),
        //     type: "string",
        //     obsTypes: [obsTypes.news, obsTypes.pews],
        // },
        {
            field: 'bloodPressure.optionalSystolicPressure',
            statusField: 'bloodPressure.status',
            description: 'BP Systolic Standing',
            type: 'numberBP',
            obsTypes: [obsTypes.news],
            optionalArm: true,
        },
        {
            field: 'bloodPressure.optionalDiastolicPressure',
            statusField: 'bloodPressure.status',
            description: 'BP Diastolic Standing',
            type: 'numberBP',
            obsTypes: [obsTypes.news],
            optionalArm: true,
        },
        // {
        //     field: "bloodPressure.optionalArm",
        //     statusField: "bloodPressure.status",
        //     description: t("BP Arm Standing"),
        //     type: "string",
        //     obsTypes: [obsTypes.news],
        // },
        {
            field: 'pulseRate.pulseRate',
            statusField: 'pulseRate.status',
            description: 'Pulse',
            type: 'number',
            scoreField: 'pulseRate.score',
            obsTypes: [obsTypes.news, obsTypes.pews],
            chart: {
                enabled: true,
                yAxis: {
                    suggestedMax: 150,
                    suggestedMin: 50,
                    title: {
                        color: observationColors.magenta,
                    },
                    ticks: {
                        color: observationColors.magenta,
                    },
                },
                dataset: {
                    backgroundColor: observationColors.magenta,
                    borderColor: colorAlpha(observationColors.magenta, 0.2),
                },
            },
        },
        {
            field: 'consciousness.consciousnessLevel.description',
            statusField: 'consciousness.status',
            description: 'Conscious',
            type: 'string',
            scoreField: 'consciousness.score',
            obsTypes: [obsTypes.news, obsTypes.pews],
        },
        {
            field: 'crt.crt',
            statusField: 'crt.status',
            description: 'CRT',
            type: 'number',
            scoreField: 'crt.score',
            obsTypes: [obsTypes.pews],
            chart: {
                enabled: true,
                yAxis: {
                    suggestedMax: 10,
                    suggestedMin: 0,
                    title: {
                        color: observationColors.purple,
                    },
                    ticks: {
                        color: observationColors.purple,
                    },
                },
                dataset: {
                    backgroundColor: observationColors.purple,
                    borderColor: colorAlpha(observationColors.purple, 0.2),
                },
            },
        },
        {
            field: 'temperature.temperature',
            statusField: 'temperature.status',
            description: 'Temp',
            type: 'number',
            precision: 1,
            scoreField: 'temperature.score',
            obsTypes: [obsTypes.news, obsTypes.pews],
            chart: {
                enabled: true,
                yAxis: {
                    suggestedMax: 45,
                    suggestedMin: 20,
                    title: {
                        color: observationColors.silver,
                    },
                    ticks: {
                        color: observationColors.silver,
                    },
                },
                dataset: {
                    backgroundColor: observationColors.silver,
                    borderColor: colorAlpha(observationColors.silver, 0.2),
                },
            },
        },
        {
            field: 'temperature.place.description',
            statusField: 'temperature.status',
            description: 'Place',
            type: 'string',
            obsTypes: [obsTypes.pews],
        },
        {
            field: 'painScore.painScore',
            statusField: 'painScore.status',
            description: 'Pain Score',
            type: 'number',
            obsTypes: [obsTypes.news, obsTypes.pews],
            chart: {
                enabled: true,
                yAxis: {
                    suggestedMax: 10,
                    suggestedMin: 0,
                    title: {
                        color: observationColors.cyan,
                    },
                    ticks: {
                        color: observationColors.cyan,
                    },
                },
                dataset: {
                    backgroundColor: observationColors.cyan,
                    borderColor: colorAlpha(observationColors.cyan, 0.2),
                },
            },
        },
        {
            field: 'eyesOpen.eyesOpenLevel.description',
            statusField: 'eyesOpen.status',
            description: 'Eye opening',
            type: 'string',
            scoreField: 'eyesOpen.score',
            obsTypes: [obsTypes.neuro],
        },
        {
            field: 'bestVerbalResponse.response.description',
            statusField: 'bestVerbalResponse.status',
            description: 'Best Verbal Response',
            scoreField: 'bestVerbalResponse.score',
            obsTypes: [obsTypes.neuro],
        },
        {
            field: 'bestMotorResponse.response.description',
            statusField: 'bestVerbalResponse.status',
            description: 'Best Motor Response',
            type: 'string',
            scoreField: 'bestMotorResponse.score',
            obsTypes: [obsTypes.neuro],
        },
        {
            field: 'pupils.reactionLeft',
            statusField: 'pupils.status',
            description: 'Pupils Reaction Left',
            type: printReactionValue,
            obsTypes: [obsTypes.neuro],
        },
        {
            field: 'pupils.sizeLeft',
            statusField: 'pupils.status',
            description: 'Pupils Size Left',
            type: printSizeValue,
            obsTypes: [obsTypes.neuro],
        },
        {
            field: 'pupils.reactionRight',
            statusField: 'pupils.status',
            description: 'Pupils Reaction Right',
            type: printReactionValue,
            obsTypes: [obsTypes.neuro],
        },
        {
            field: 'pupils.sizeRight',
            statusField: 'pupils.status',
            description: 'Pupils Size Right',
            type: printSizeValue,
            obsTypes: [obsTypes.neuro],
        },
        {
            field: 'limbMovementArms.arm',
            statusField: 'limbMovementArms.status',
            description: 'First Arm',
            type: 'string',
            obsTypes: [obsTypes.neuro],
        },
        {
            field: 'limbMovementArms.movement.description',
            statusField: 'limbMovementArms.status',
            description: 'First Arm Movement',
            type: 'string',
            obsTypes: [obsTypes.neuro],
        },
        {
            field: 'limbMovementArms.secondArm',
            statusField: 'limbMovementArms.status',
            description: 'Second Arm',
            type: 'string',
            obsTypes: [obsTypes.neuro],
        },
        {
            field: 'limbMovementArms.secondMovement.description',
            statusField: 'limbMovementArms.status',
            description: 'Second Arm Movement',
            type: 'string',
            obsTypes: [obsTypes.neuro],
        },
        {
            field: 'limbMovementLegs.leg',
            statusField: 'limbMovementLegs.status',
            description: 'First Leg',
            type: 'string',
            obsTypes: [obsTypes.neuro],
        },
        {
            field: 'limbMovementLegs.movement.description',
            statusField: 'limbMovementLegs.status',
            description: 'First Leg Movement',
            type: 'string',
            obsTypes: [obsTypes.neuro],
        },
        {
            field: 'limbMovementLegs.secondLeg',
            statusField: 'limbMovementLegs.status',
            description: 'Second Leg',
            type: 'string',
            obsTypes: [obsTypes.neuro],
        },
        {
            field: 'limbMovementLegs.secondMovement.description',
            statusField: 'limbMovementLegs.status',
            description: 'Second Leg Movement',
            type: 'string',
            obsTypes: [obsTypes.neuro],
        },
        {
            field: 'glucose.glucose',
            statusField: 'glucose.status',
            description: 'Blood Glucose',
            type: 'string',
            obsTypes: [obsTypes.news, obsTypes.pews, obsTypes.neuro],
            chart: {
                enabled: true,
                yAxis: {
                    suggestedMax: 100,
                    suggestedMin: 0,
                    title: {
                        color: observationColors.teal,
                    },
                    ticks: {
                        color: observationColors.teal,
                    },
                },
                dataset: {
                    backgroundColor: observationColors.teal,
                    borderColor: colorAlpha(observationColors.teal, 0.2),
                },
            },
        },
        {
            field: 'glucose.ketones',
            statusField: 'glucose.status',
            description: 'Ketones',
            type: 'string',
            obsTypes: [obsTypes.news, obsTypes.pews],
        },
        {
            field: 'completedDate',
            description: 'Submitted',
            type: 'date',
            format: defaultDateTimeFormat,
            disableOnTable: !isRetroObs,
        },
        {
            field: 'completedByFullName',
            description: 'By',
            type: 'string',
        },
        {
            field: 'nextObsDate',
            description: 'Next Obs',
            type: 'date',
            obsTypes: _getNextObsTypes(),
            format: defaultDateTimeFormat,
        },
        {
            field: 'nextObsStatus',
            description: 'Next Obs Status',
            type: 'nextObs',
            obsTypes: [obsTypes.news, obsTypes.pews],
        },
        {
            field: 'nextObsReason',
            description: 'Next Obs Reason',
            type: 'string',
            obsTypes: [obsTypes.news, obsTypes.pews],
        },
        {
            field: 'extraOption1',
            description: newRiskAssessment ? 'Nursing Instinct' : 'Escalation - Concern',
            type: 'boolean',
            obsTypes: [obsTypes.news, obsTypes.pews],
        },
        {
            field: 'extraOption1Reason.tooltip',
            description: 'Nursing Instinct Reason',
            type: 'string',
            obsTypes: [newRiskAssessment ? obsTypes.pews : ''],
        },
        {
            field: 'extraOption4',
            description: 'Family Concern',
            type: 'boolean',
            obsTypes: [newRiskAssessment ? obsTypes.pews : ''],
        },
        {
            field: 'extraOption4Reason.tooltip',
            description: 'Family Concern Reason',
            type: 'string',
            obsTypes: [newRiskAssessment ? obsTypes.pews : ''],
        },
        {
            field: 'extraOption2',
            description: 'Escalation - Score 5+',
            type: 'boolean',
            obsTypes: [obsTypes.news, obsTypes.pews],
        },
        {
            field: 'extraOption3',
            description: 'Sepsis Suspected',
            type: 'boolean',
            obsTypes: [obsTypes.news, obsTypes.pews],
        },
        {
            field: 'extraOption3Reason',
            description: 'Sepsis Suspected - Reason',
            type: 'string',
            obsTypes: [obsTypes.news, obsTypes.pews],
        },
    ];
}

export const getFullObsName = (type) => {
    let name = '';
    switch (type) {
        case 'news':
            name = 'National Early Warning Score';
            break;
        case 'pews':
            name = 'Pediatric Early Warning Score';
            break;
        default:
            break;
    }
    return name;
};